import { MenuItem } from "primereact/menuitem";
import * as React from "react";
import { useContext, useMemo } from "react";
import * as client from "./client";
import { ClientId, makeClientId } from "./client-features";
import styles from "./MenuBar.module.css";
import { defaultFilterForLobsAndArchived } from "./pages/specialty-assistant/dashboard/filter";
import { ROUTES } from "./routes";
import { LOB } from "./api/routes";

export type ClientConfig = {
  isAsgardEnabled: boolean;
  isBrokerAnalyticsEnabled: boolean;
  menuItems: (
    user: client.User | undefined,
    currentRoute: string
  ) => MenuItem[];
  defaultRoute: (user: client.User | undefined) => string;
};

type Navigate = (route: string) => void;
type MakeMenuItem = (
  navigate: Navigate,
  route: string,
  routeToNavigateTo: string
) => MenuItem;

const menuClassname = (route: string, current: string): string | undefined => {
  return route == current ? styles.highlighted : undefined;
};

const commonProperties = (
  navigate: Navigate,
  route: string,
  current: string
): Partial<MenuItem> => {
  return {
    command: () => {
      navigate(route);
    },
    className: menuClassname(route, current),
  };
};

const propertySubmissionsRoute = (statuses: client.status[] = []) => {
  return ROUTES.DASHBOARD_WITH_FILTER(
    defaultFilterForLobsAndArchived(["property"], statuses)
  );
};

const PROPERTY_MENU_ITEM: MakeMenuItem = (
  navigate,
  current,
  routeToNavigateTo
) => ({
  id: "property",
  key: "property",
  label: "Property Dashboard",
  icon: "pi pi-building",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

const catMenuRoute = () => {
  return ROUTES.DASHBOARD_WITH_FILTER(defaultFilterForLobsAndArchived(["cat"]));
};

const CAT_MENU_ITEM: MakeMenuItem = (navigate, current, routeToNavigateTo) => ({
  id: "CAT",
  key: "cat",
  label: "CAT Dashboard",
  icon: "pi pi-bolt",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

const mySubmissionsRoute = (
  lobs: LOB[],
  statuses: client.status[],
  userId: string
) => {
  return ROUTES.DASHBOARD_WITH_FILTER(
    defaultFilterForLobsAndArchived(lobs, statuses, [userId])
  );
};

const MY_SUBMISSIONS_MENU_ITEM: MakeMenuItem = (
  navigate,
  current,
  routeToNavigateTo
) => ({
  id: "my_submissions",
  key: "my_submissions",
  label: "Assigned to Me",
  icon: "pi pi-user",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

const readyForAssignmentRoute = (lobs: LOB[]) => {
  return ROUTES.DASHBOARD_WITH_FILTER(
    defaultFilterForLobsAndArchived(lobs, ["cleared"], ["Unassigned"])
  );
};

const READY_FOR_ASSIGNMENT_MENU_ITEM: MakeMenuItem = (
  navigate,
  current,
  routeToNavigateTo
) => ({
  id: "ready_for_assignment",
  key: "ready_for_assignment",
  label: "Ready for Assignment",
  icon: "pi pi-inbox",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

const ALL_SUBMISSIONS_MENU_ITEM: MakeMenuItem = (
  navigate,
  current,
  routeToNavigateTo
) => ({
  id: "all_submissions",
  key: "all_submissions",
  label: "All Submissions",
  icon: "pi pi-globe",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

const policyFlyMenuItem = (navigate: Navigate): MenuItem => ({
  id: "policy_fly",
  label: "Policy Fly",
  icon: "pi pi-dollar",
  items: [
    {
      id: "policy_fly_quoted",
      label: "Quoted",
      command: () => navigate(ROUTES.DASHBOARD_POLICYFLY + "?status=QUOTED"),
    },
    {
      id: "policy_fly_bound",
      label: "Bound",
      command: () => navigate(ROUTES.DASHBOARD_POLICYFLY + "?status=SUBMITTED"),
    },
    {
      id: "policy_fly_in_force",
      label: "In Force",
      command: () => navigate(ROUTES.DASHBOARD_POLICYFLY + "?status=IN_FORCE"),
    },
  ],
});

const HISTORICAL_SUBMISSIONS_MENU_ITEM: MakeMenuItem = (
  navigate,
  current,
  routeToNavigateTo
) => ({
  id: "historical_submissions",
  key: "historical_submissions",
  label: "Historical Submissions",
  icon: "pi pi-folder-open",
  ...commonProperties(navigate, routeToNavigateTo, current),
});

export const featuresForClientId = (
  clientId: string,
  navigate: Navigate
): ClientConfig => {
  switch (clientId) {
    case "pantheon":
      return {
        isAsgardEnabled: true,
        isBrokerAnalyticsEnabled: true,
        menuItems: (_, route) => [
          PROPERTY_MENU_ITEM(navigate, route, propertySubmissionsRoute()),
          CAT_MENU_ITEM(navigate, route, catMenuRoute()),
        ],
        defaultRoute: () =>
          ROUTES.DASHBOARD_WITH_FILTER(
            defaultFilterForLobsAndArchived(["property"])
          ),
      };

    case "luminor_dev":
    case "luminor":
      return {
        isAsgardEnabled: true,
        isBrokerAnalyticsEnabled: false,
        menuItems: (u, route) =>
          u
            ? [
                MY_SUBMISSIONS_MENU_ITEM(
                  navigate,
                  route,
                  mySubmissionsRoute(
                    u.lobs as LOB[],
                    [
                      "cleared",
                      "pending",
                      "indicated",
                      "quoted",
                      "bound",
                      "issued",
                    ],
                    u.id
                  )
                ),
                READY_FOR_ASSIGNMENT_MENU_ITEM(
                  navigate,
                  route,
                  readyForAssignmentRoute(u.lobs as LOB[])
                ),
                ALL_SUBMISSIONS_MENU_ITEM(
                  navigate,
                  route,
                  ROUTES.DASHBOARD_WITH_FILTER(
                    defaultFilterForLobsAndArchived(["cat"])
                  )
                ),
                HISTORICAL_SUBMISSIONS_MENU_ITEM(
                  navigate,
                  route,
                  ROUTES.DASHBOARD_WITH_FILTER(
                    defaultFilterForLobsAndArchived(
                      ["cat"],
                      [
                        "not_cleared_effective_date_too_far",
                        "not_cleared_incomplete_submission",
                        "not_cleared_out_of_appetite",
                        "not_cleared_prior_submission",
                        "not_cleared_unapproved_broker",
                        "not_cleared_foreign_exposure",
                        "lost",
                        "declined",
                      ]
                    )
                  )
                ),
                policyFlyMenuItem(navigate),
              ]
            : [],
        defaultRoute: () =>
          ROUTES.DASHBOARD_WITH_FILTER(
            defaultFilterForLobsAndArchived(["cat"])
          ),
      };
  }

  throw new Error(`unknown clientId ${clientId}`);
};

export const useFeaturesForClientId = (
  clientId: string,
  navigate: Navigate
): ClientConfig => {
  return useMemo(
    () => featuresForClientId(clientId, navigate),
    [clientId, navigate]
  );
};

export const ClientIdContext = React.createContext<string | null>(null);
export const useClientId = (): ClientId => {
  const clientId = useContext(ClientIdContext);
  if (!clientId) {
    throw new Error("Client ID Context not provided");
  }
  return makeClientId(clientId);
};
